import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Phone } from 'lucide-react';
import axios from 'axios';
import { CONTACT } from '../lyphline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ApproveQuote = () => {
  const [params] = useSearchParams();
  const id = params.get('id');
  const token = params.get('token');
  const isDecline = params.get('action') === 'decline';
  const [state, setState] = useState('loading'); // loading | success | already | declined | already_declined | error
  const [data, setData] = useState({});

  useEffect(() => {
    if (!id || !token) { setState('error'); return; }
    const endpoint = isDecline ? 'decline' : 'approve';
    axios.post(`${API_URL}/api/repair-quotes/${id}/${endpoint}`, { token })
      .then((res) => {
        setData(res.data || {});
        if (isDecline) setState(res.data?.already_declined ? 'already_declined' : 'declined');
        else setState(res.data?.already_approved ? 'already' : 'success');
      })
      .catch(() => setState('error'));
  }, [id, token, isDecline]);

  const firstName = data.name ? String(data.name).split(' ')[0] : '';
  const declined = state === 'declined' || state === 'already_declined';
  const [reason, setReason] = useState('');
  const [reasonSaved, setReasonSaved] = useState(false);

  const DECLINE_REASONS = ['Too expensive', 'Went with someone else', 'Just browsing / not ready', 'Decided to replace the machine', 'Other'];

  const submitReason = (value) => {
    setReason(value);
    if (!value) return;
    axios.post(`${API_URL}/api/repair-quotes/${id}/decline-reason`, { token, reason: value })
      .then(() => setReasonSaved(true))
      .catch(() => {});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003456] to-[#002B47] flex items-center justify-center px-6" data-testid="approve-quote-page">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 text-center">
        {state === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto mb-4 text-[#CE8946] animate-spin" />
            <p className="text-[#003456] font-medium">{isDecline ? 'Updating your request…' : 'Confirming your approval…'}</p>
          </>
        )}

        {(state === 'success' || state === 'already') && (
          <div data-testid="approve-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#003456] mb-2">
              {state === 'already' ? 'Already approved' : 'Quote approved!'}
            </h1>
            <p className="text-gray-600">
              {firstName ? `Thanks, ${firstName}. ` : ''}
              {state === 'already'
                ? 'We already have your go-ahead and your rebuild is in our queue.'
                : "We've got the green light and your rebuild is now in our queue."}
              {data.quote_amount ? ` Your approved quote: ${data.quote_amount}.` : ''}
            </p>
            <p className="text-gray-600 mt-3">Our team will be in touch shortly with next steps and shipping details.</p>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-2 mt-6 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors"
              data-testid="approve-call-btn"
            >
              <Phone className="h-4 w-4" /> Call us: {CONTACT.phone}
            </a>
          </div>
        )}

        {declined && (
          <div data-testid="decline-success">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-9 w-9 text-slate-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#003456] mb-2">Thanks for letting us know</h1>
            <p className="text-gray-600">
              {firstName ? `No problem, ${firstName}. ` : ''}
              We've noted that you're not moving ahead right now. If anything changes, your quote is here whenever you're ready.
            </p>
            <p className="text-gray-600 mt-3">Questions or changed your mind? We're happy to help.</p>
            <div className="mt-6 text-left bg-slate-50 border border-slate-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-[#003456] mb-2">Mind sharing why? (optional)</label>
              {reasonSaved ? (
                <p className="text-sm text-green-700 font-medium" data-testid="decline-reason-saved">Thanks for the feedback!</p>
              ) : (
                <select
                  value={reason}
                  onChange={(e) => submitReason(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#CE8946] bg-white"
                  data-testid="decline-reason-select"
                >
                  <option value="">Select a reason…</option>
                  {DECLINE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              )}
            </div>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-2 mt-6 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-3 rounded-md transition-colors"
              data-testid="decline-call-btn"
            >
              <Phone className="h-4 w-4" /> Call us: {CONTACT.phone}
            </a>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="approve-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <XCircle className="h-9 w-9 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#003456] mb-2">Link not valid</h1>
            <p className="text-gray-600">
              This approval link is invalid or has expired. Please reply to your quote email or call us and we'll confirm your rebuild right away.
            </p>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-2 mt-6 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-3 rounded-md transition-colors"
            >
              <Phone className="h-4 w-4" /> Call us: {CONTACT.phone}
            </a>
          </div>
        )}

        <div className="mt-8">
          <Link to="/" className="text-sm text-gray-400 hover:text-[#003456]">← Back to LyphLine</Link>
        </div>
      </div>
    </div>
  );
};

export default ApproveQuote;
